import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Drawer,
  EmptyState,
  PageHeader,
  Spinner,
  Stat,
  Tabs,
  useConfirm,
  useToast,
} from "../../design-system";
import {
  archiveDocument,
  cancelDocumentRequest,
  deleteDocument,
  downloadDocument,
  getDocumentCategories,
  getDocumentRequests,
  getDocuments,
  publishDocument,
  unarchiveDocument,
} from "../../helpers/documents_helper";
import { getEmployees } from "../../helpers/kronaly_helper";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import { fullName, getExpiryStatus, unwrap } from "./helpers";
import { useDocumentDetail } from "./useDocumentDetail";
import { DocumentsTable } from "./components/DocumentsTable";
import { DocumentsFilterBar } from "./components/DocumentsFilterBar";
import { DocumentDetail } from "./components/DocumentDetail";
import { UploadDocumentDrawer } from "./components/UploadDocumentDrawer";
import { RequestDocumentDrawer } from "./components/RequestDocumentDrawer";
import { FulfillRequestDrawer } from "./components/FulfillRequestDrawer";
import { PendingRequestsBanner } from "./components/PendingRequestsBanner";

function Documentos() {
  document.title = "Documentos | Kronaly";
  const toast = useToast();
  const confirm = useConfirm();
  const { can } = useCurrentMembership();
  // "Manager view" del módulo Documentos = capability fina.
  const isManager = can("documents:manage");

  const [searchParams, setSearchParams] = useSearchParams();
  const employeeFilter = searchParams.get("employee_profile_id") || "";

  const [tab, setTab] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [expiryFilter, setExpiryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);

  const clearEmployeeFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("employee_profile_id");
    setSearchParams(next, { replace: true });
  };

  const [docs, setDocs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openDoc, setOpenDoc] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [fulfillingRequest, setFulfillingRequest] = useState(null);
  const [requests, setRequests] = useState([]);

  const { events, eventsLoading, previewUrl, previewLoading, previewError } =
    useDocumentDetail(openDoc);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { include_archived: 1 };
      const [docsRes, empRes, catRes, reqRes] = await Promise.all([
        getDocuments(params),
        getEmployees().catch(() => ({ data: [] })),
        getDocumentCategories().catch(() => ({ data: [] })),
        getDocumentRequests().catch(() => ({ data: [] })),
      ]);
      setDocs(unwrap(docsRes) || []);
      setEmployees(unwrap(empRes) || []);
      setCategories(unwrap(catRes) || []);
      setRequests(unwrap(reqRes) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los documentos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const employeesById = useMemo(() => {
    const map = {};
    employees.forEach((e) => {
      if (e?.id) map[e.id] = e;
    });
    return map;
  }, [employees]);

  const counts = useMemo(
    () => ({
      all: docs.length,
      draft: docs.filter((d) => d.status === "draft").length,
      published: docs.filter((d) => d.status === "published").length,
      archived: docs.filter((d) => d.status === "archived").length,
    }),
    [docs],
  );

  // Para filtrar por categoría root incluyendo sus subcategorías.
  const childrenByRoot = useMemo(() => {
    const map = {};
    (categories || []).forEach((root) => {
      map[root.id] = (root.children || []).map((c) => c.id);
    });
    return map;
  }, [categories]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs
      .filter((d) => (employeeFilter ? d.employee_profile_id === employeeFilter : true))
      .filter((d) => (tab === "all" ? true : d.status === tab))
      .filter((d) => {
        if (categoryFilter === "all") return true;
        if (d.category_id === categoryFilter) return true;
        const childIds = childrenByRoot[categoryFilter] || [];
        return childIds.includes(d.category_id);
      })
      .filter((d) => {
        if (expiryFilter === "all") return true;
        const s = getExpiryStatus(d.expires_at);
        if (expiryFilter === "expired") return s === "expired";
        if (expiryFilter === "soon") return s === "soon";
        if (expiryFilter === "valid") return s === "ok" || s === "none";
        return true;
      })
      .filter((d) => {
        if (!q) return true;
        const emp = employeesById[d.employee_profile_id];
        const haystack = [
          d.title,
          d.original_filename,
          d.description,
          fullName(emp),
          emp?.email,
          d.category?.name,
          d.category?.parent_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => (b.published_at || b.id).localeCompare(a.published_at || a.id));
  }, [docs, tab, categoryFilter, expiryFilter, search, employeesById, employeeFilter, childrenByRoot]);

  const employeeFilterName = employeeFilter
    ? fullName(employeesById[employeeFilter])
    : null;

  const onDownload = async (doc) => {
    try {
      await downloadDocument(doc.id, doc.original_filename || doc.title);
    } catch (e) {
      toast.error("No se pudo descargar", typeof e === "string" ? e : e?.message);
    }
  };

  const onPublish = async (doc) => {
    try {
      await publishDocument(doc.id);
      toast.success("Documento publicado");
      load();
    } catch (e) {
      toast.error("No se pudo publicar", typeof e === "string" ? e : e?.message);
    }
  };

  const onArchive = async (doc) => {
    const ok = await confirm({
      title: "Archivar documento",
      description: `Archivar "${doc.title}". Dejará de aparecer en los listados activos.`,
      confirmLabel: "Archivar",
      cancelLabel: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await archiveDocument(doc.id);
      toast.success("Documento archivado");
      setOpenDoc(null);
      load();
    } catch (e) {
      toast.error("No se pudo archivar", typeof e === "string" ? e : e?.message);
    }
  };

  const onUnarchive = async (doc) => {
    try {
      const res = await unarchiveDocument(doc.id);
      const updated = unwrap(res);
      toast.success("Documento desarchivado");
      setOpenDoc(updated || null);
      load();
    } catch (e) {
      toast.error("No se pudo desarchivar", typeof e === "string" ? e : e?.message);
    }
  };

  const onDelete = async (doc) => {
    const ok = await confirm({
      title: "Eliminar documento",
      description: `Eliminar "${doc.title}". El archivo se borrará del almacenamiento.`,
      confirmLabel: "Eliminar",
      cancelLabel: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteDocument(doc.id);
      toast.success("Documento eliminado");
      setOpenDoc(null);
      load();
    } catch (e) {
      toast.error("No se pudo eliminar", typeof e === "string" ? e : e?.message);
    }
  };

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow={isManager ? "Gestión documental" : "Mis documentos"}
        title="Documentos"
        description={
          isManager
            ? "Sube, publica y archiva documentos del workspace y de cada empleado."
            : "Consulta y descarga los documentos publicados disponibles para ti."
        }
        actions={
          isManager ? (
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" icon="Inbox" onClick={() => setShowRequest(true)}>
                Solicitar documento
              </Button>
              <Button variant="primary" icon="Upload" onClick={() => setShowUpload(true)}>
                Subir documento
              </Button>
            </div>
          ) : null
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}

      <PendingRequestsBanner
        requests={requests}
        isManager={isManager}
        onFulfill={(req) => setFulfillingRequest(req)}
        onCancel={async (req) => {
          try {
            await cancelDocumentRequest(req.id);
            toast.success("Solicitud cancelada");
            load();
          } catch (e) {
            toast.error("No se pudo cancelar", typeof e === "string" ? e : e?.message);
          }
        }}
      />

      {isManager && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <Stat label="Total" value={counts.all} />
          <Stat label="Borradores" value={counts.draft} />
          <Stat label="Publicados" value={counts.published} />
          <Stat label="Archivados" value={counts.archived} />
        </div>
      )}

      <Card padded={false}>
        <div style={{ padding: "12px 16px 0" }}>
          <Tabs
            tabs={[
              { id: "all", label: "Todos", count: counts.all },
              ...(isManager ? [{ id: "draft", label: "Borradores", count: counts.draft }] : []),
              { id: "published", label: "Publicados", count: counts.published },
              ...(isManager ? [{ id: "archived", label: "Archivados", count: counts.archived }] : []),
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>

        <DocumentsFilterBar
          search={search}
          onSearchChange={setSearch}
          employeeFilter={employeeFilter}
          employeeFilterName={employeeFilterName}
          onClearEmployeeFilter={clearEmployeeFilter}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          categories={categories}
          expiryFilter={expiryFilter}
          onExpiryFilterChange={setExpiryFilter}
          resultsCount={filtered.length}
        />

        {loading ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <Spinner size={16} />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="FileText"
            title={search.trim() ? "Sin resultados" : "Aún no hay documentos"}
            description={
              search.trim()
                ? "Prueba con otra búsqueda o cambia los filtros."
                : isManager
                ? "Sube el primer documento desde el botón superior."
                : "Cuando RRHH publique documentos para ti aparecerán aquí."
            }
          />
        ) : (
          <DocumentsTable
            rows={filtered}
            employeesById={employeesById}
            isManager={isManager}
            onOpen={setOpenDoc}
            onDownload={onDownload}
          />
        )}
      </Card>

      <Drawer
        open={!!openDoc}
        onClose={() => setOpenDoc(null)}
        size="lg"
        title={openDoc?.title || "Documento"}
        footer={
          openDoc ? (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Button variant="ghost" icon="Download" onClick={() => onDownload(openDoc)}>
                Descargar
              </Button>
              {isManager && (
                <div style={{ display: "flex", gap: 8 }}>
                  {openDoc.status === "draft" && (
                    <Button variant="primary" icon="Check" onClick={() => onPublish(openDoc)}>
                      Publicar
                    </Button>
                  )}
                  {openDoc.status === "archived" ? (
                    <Button variant="secondary" icon="ArchiveRestore" onClick={() => onUnarchive(openDoc)}>
                      Desarchivar
                    </Button>
                  ) : (
                    <Button variant="dangerSoft" icon="Archive" onClick={() => onArchive(openDoc)}>
                      Archivar
                    </Button>
                  )}
                  <Button variant="dangerSoft" icon="Trash2" onClick={() => onDelete(openDoc)}>
                    Eliminar
                  </Button>
                </div>
              )}
            </div>
          ) : null
        }
      >
        {openDoc && (
          <DocumentDetail
            doc={openDoc}
            employee={employeesById[openDoc.employee_profile_id]}
            previewUrl={previewUrl}
            previewLoading={previewLoading}
            previewError={previewError}
            events={events}
            eventsLoading={eventsLoading}
          />
        )}
      </Drawer>

      <UploadDocumentDrawer
        open={showUpload}
        onClose={() => setShowUpload(false)}
        employees={employees}
        categories={categories}
        defaultEmployeeProfileId={employeeFilter || ""}
        onUploaded={() => {
          setShowUpload(false);
          load();
        }}
      />

      <RequestDocumentDrawer
        open={showRequest}
        onClose={() => setShowRequest(false)}
        employees={employees}
        categories={categories}
        defaultEmployeeProfileId={employeeFilter || ""}
        onCreated={() => {
          setShowRequest(false);
          load();
        }}
      />

      <FulfillRequestDrawer
        request={fulfillingRequest}
        onClose={() => setFulfillingRequest(null)}
        onFulfilled={() => {
          setFulfillingRequest(null);
          load();
        }}
      />
    </div>
  );
}

export default Documentos;
